/* Global in-context credit top-up dialog.
 *
 * Opens on the window event `clinq:topup` (detail: { needed, balance }) —
 * fired by the api.js 402 interceptor's "Buy credits" toast CTA and the
 * LowCreditNudge. Pre-selects the cheapest pack that covers the shortfall,
 * runs the Paystack inline popup, verifies the payment in place and awards
 * credits WITHOUT leaving the page — so founders can retry their action
 * immediately.
 */
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { openPaystackPopup } from "@/lib/paystackInline";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Sparkle, CheckCircle, ShieldCheck } from "@phosphor-icons/react";

const C = { ink: "#0A2826", teal: "#0A8B85", tealSoft: "#7BD3CE", orange: "#F59E0B" };

export default function TopUpModal() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [shortfall, setShortfall] = useState(null); // credits still missing
  const [balance, setBalance] = useState(null);
  const [packs, setPacks] = useState(null);
  const [selected, setSelected] = useState("");
  const [phase, setPhase] = useState("pick"); // pick | paying | verifying

  const load = useCallback(async (detail) => {
    try {
      const [{ data: p }, { data: me }] = await Promise.all([
        api.get("/credits/packs"),
        api.get("/credits/me"),
      ]);
      const bal = Number(me?.balance ?? 0);
      const missing = detail?.needed != null ? Math.max(0, Number(detail.needed) - bal) : null;
      setPacks(p.packs || []);
      setBalance(bal);
      setShortfall(missing);
      const sorted = [...(p.packs || [])].sort((a, b) => a.credits - b.credits);
      const fit = missing != null ? sorted.find((pk) => pk.credits >= missing) : null;
      setSelected((fit || sorted.find((pk) => pk.highlighted) || sorted[0])?.id || "");
    } catch {
      toast.error("Could not load credit packs");
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    const onTopUp = (e) => {
      setPacks(null);
      setPhase("pick");
      setOpen(true);
      load(e?.detail || {});
    };
    window.addEventListener("clinq:topup", onTopUp);
    return () => window.removeEventListener("clinq:topup", onTopUp);
  }, [load]);

  const verifyAndAward = async (reference) => {
    setPhase("verifying");
    for (let i = 0; i < 8; i++) {
      try {
        const { data } = await api.get(`/credits/verify/${reference}`);
        if (data.status === "success") {
          window.dispatchEvent(new Event("clinq:credits-changed"));
          // Signals api.js to replay the action that was blocked by the 402.
          window.dispatchEvent(new Event("clinq:credits-topped-up"));
          let bal = null;
          try { bal = (await api.get("/credits/me")).data?.balance; } catch { /* chip will refresh */ }
          toast.success(
            bal != null ? `Credits added — your balance is now ${bal}.` : "Credits added.",
            { duration: 8000 }
          );
          setOpen(false);
          return;
        }
        if (["failed", "abandoned", "reversed"].includes(data.status)) {
          toast.error("Payment didn't complete. You weren't charged — try again.");
          setPhase("pick");
          return;
        }
      } catch { /* keep polling */ }
      await new Promise((r) => setTimeout(r, 2000));
    }
    toast.info("Payment is still processing — your credits will appear shortly.");
    window.dispatchEvent(new Event("clinq:topup-closed"));
    setOpen(false);
  };

  const pay = async () => {
    const pack = (packs || []).find((p) => p.id === selected);
    if (!pack) return;
    setPhase("paying");
    try {
      const callbackUrl = `${window.location.origin}/payment/callback`;
      const { data } = await api.post("/credits/checkout", {
        pack_id: pack.id,
        currency: "KES",
        callback_url: callbackUrl,
      });
      await openPaystackPopup({
        accessCode: data.access_code,
        publicKey: data.public_key,
        reference: data.reference,
        authorizationUrl: data.authorization_url,
        onSuccess: () => verifyAndAward(data.reference),
        onClose: () => {
          setPhase("pick");
          toast.info("Checkout closed. You can try again anytime.");
        },
      });
    } catch {
      toast.error("Could not start checkout — please try again.");
      setPhase("pick");
    }
  };

  const seeAll = () => {
    window.dispatchEvent(new Event("clinq:topup-closed"));
    setOpen(false);
    navigate("/credits");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => {
      if (phase === "verifying") return;
      if (!v) window.dispatchEvent(new Event("clinq:topup-closed"));
      setOpen(v);
    }}>
      <DialogContent className="max-w-md border-0 p-0 overflow-hidden text-white z-[120]"
        style={{ backgroundColor: C.ink, border: "1px solid rgba(123,211,206,0.22)", fontFamily: "Manrope, sans-serif" }}
        data-testid="topup-modal">
        <div className="p-6">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: C.orange }}>
            Quick top-up
          </div>
          <DialogTitle asChild>
            <h2 className="font-extrabold text-2xl mt-1.5 tracking-tight text-white">
              {shortfall ? `You're ${shortfall} credit${shortfall === 1 ? "" : "s"} short` : "Top up your credits"}
            </h2>
          </DialogTitle>
          <p className="text-[13px] text-white/60 mt-1.5" data-testid="topup-shortfall">
            {balance != null && <>Current balance: <span className="font-bold" style={{ color: C.tealSoft }}>{balance}</span>. </>}
            Buy a pack below — your action finishes automatically the moment payment clears.
          </p>

          {phase === "verifying" ? (
            <div className="mt-6 rounded-xl border p-6 text-center" data-testid="topup-verifying"
              style={{ borderColor: "rgba(123,211,206,0.2)", backgroundColor: "rgba(0,0,0,0.3)" }}>
              <div className="animate-pulse text-sm font-semibold" style={{ color: C.tealSoft }}>
                Confirming your payment…
              </div>
              <div className="text-[11px] text-white/45 mt-1.5">Usually a few seconds — don't close this window.</div>
            </div>
          ) : !packs ? (
            <div className="mt-6 text-sm text-white/45 text-center py-8" data-testid="topup-loading">Loading packs…</div>
          ) : (
            <>
              <div className="mt-5 space-y-2">
                {[...packs].sort((a, b) => a.credits - b.credits).map((p) => {
                  const price = p.prices?.[0];
                  const active = selected === p.id;
                  const covers = shortfall != null && p.credits >= shortfall;
                  return (
                    <button key={p.id} onClick={() => setSelected(p.id)} disabled={phase === "paying"}
                      data-testid={`topup-pack-${p.id}`}
                      className="w-full flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors"
                      style={{
                        borderColor: active ? C.teal : "rgba(123,211,206,0.15)",
                        backgroundColor: active ? "rgba(10,139,133,0.14)" : "rgba(0,0,0,0.25)",
                      }}>
                      <span className="w-4 h-4 rounded-full border-2 shrink-0 grid place-items-center"
                        style={{ borderColor: active ? C.teal : "rgba(255,255,255,0.3)" }}>
                        {active && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: C.teal }} />}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="flex items-center gap-2">
                          <span className="font-extrabold text-[15px]">{p.credits} credits</span>
                          <span className="text-[11px] text-white/45">{p.label}</span>
                          {covers && (
                            <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.12em] px-1.5 py-0.5 rounded"
                              style={{ backgroundColor: "rgba(34,197,94,0.15)", color: "#4ADE80" }}>
                              <CheckCircle size={9} weight="fill" /> Covers it
                            </span>
                          )}
                        </span>
                      </span>
                      <span className="font-bold text-sm tabular-nums shrink-0" style={{ color: active ? C.tealSoft : "rgba(255,255,255,0.7)" }}>
                        {price?.display}
                      </span>
                    </button>
                  );
                })}
              </div>

              <button onClick={pay} disabled={phase === "paying" || !selected} data-testid="topup-pay-btn"
                className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md text-sm font-extrabold transition-colors disabled:opacity-60"
                style={{ backgroundColor: C.orange, color: "#000" }}>
                <Sparkle size={14} weight="fill" />
                {phase === "paying" ? "Opening secure checkout…" : "Pay with Paystack"}
              </button>

              <div className="mt-3 flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 text-[10px] text-white/40">
                  <ShieldCheck size={11} weight="fill" style={{ color: C.tealSoft }} />
                  M-Pesa &amp; card · secured by Paystack
                </span>
                <button onClick={seeAll} data-testid="topup-see-all"
                  className="text-[11px] font-semibold hover:underline" style={{ color: C.tealSoft }}>
                  See all packs
                </button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
